import React, { useState, useEffect, useCallback } from "react";
import "./css/index.css";
import { FiCopy, FiUnlock, FiLock, FiX, FiPlus, FiFile, FiImage, FiLink, FiCode } from "react-icons/fi";
import { SiTailwindcss, SiCss3 } from "react-icons/si";
import { FaPalette } from "react-icons/fa";
import { Navbar } from "../Header/NavBar.js"
import { useParams } from "react-router-dom";
import { useColorModes, useExportPalette } from "./hooks";


const HEX_CHARS = [
  "1","2","3","4","5","6","7","8","9",
  "A","B","C","D","E","F"
];

export const RandomColorGenerator = () => {
 const {
    hexToHSL,
    getMonochrome,
    getComplementary,
    getAnalogous,
    getSplitComplementary,
    getTriadic,
  } = useColorModes();

  const { hexCode } = useParams();

  /* ==================== STATE ==================== */
  const [numColors, setNumColors] = useState(5);
  const [lockedColors, setLockedColors] = useState([]);
  const [hoveredColor, setHoveredColor] = useState(null);
  const [colors, setColors] = useState([]);
  const [copied, setCopied] = useState(false);
  const [openExport, setOpenExport] = useState(false);
  const [activePaletteIndex, setActivePaletteIndex] = useState(null);
  const [historyCount, setHistoryCount] = useState(0);
  const [showModes, setShowModes] = useState(false);
  const [activeMode, setActiveMode] = useState("Random");
  const [history, setHistory] = useState([]);
  const [showHistory, setShowHistory] = useState(false);
  const colorModes = [
  "Random",
  "Monochrome",
  "Complementary",
  "Analogous",
  "Triadic",
  "Split Complementary",
];

  const {
    exportAsSVG,
    exportAsPNG,
    copyAsURL,
    downloadTailwindV4,
    downloadJSON,
    downloadCSSVariables,
  } = useExportPalette(colors, setCopied);

  const handleExport = () => {
  if (!openExport) setShowModes(false);
  setOpenExport((prev) => !prev);
};

const handleColorModes = () => {
  if (!showModes) setOpenExport(false);
  setShowModes((prev) => !prev);
};

const handleHistoryClick = () => {
  setShowHistory((prev) => !prev);
};

const changeColorModes = (mode) => {
  setActiveMode(mode);

  const baseColor = generateHexColor();
  let newColors = [];

  switch (mode) {
    case "Random":
      newColors = generateRandomColors(numColors);
      break;
    case "Monochrome":
      newColors = getMonochrome(baseColor, numColors);
      break;
    case "Complementary":
      newColors = getComplementary(baseColor, numColors);
      break;
    case "Analogous":
      newColors = getAnalogous(baseColor, numColors);
      break;
    case "Triadic":
      newColors = getTriadic(baseColor, numColors);
      break;
    case "Split Complementary":
      newColors = getSplitComplementary(baseColor, numColors);
      break;
    default:
      newColors = generateRandomColors(numColors);
  }

  setColors(newColors);
  setShowModes(false);
};

const hexToRgb = (hex) => {
  hex = hex.replace("#", "");
  return {
    r: parseInt(hex.substring(0, 2), 16),
    g: parseInt(hex.substring(2, 4), 16),
    b: parseInt(hex.substring(4, 6), 16),
  };
};

const rgbToHex = ({ r, g, b }) => {
  const toHex = (x) => x.toString(16).padStart(2, "0");
  return `#${toHex(r)}${toHex(g)}${toHex(b)}`;
};

const generateGradient = (hex, steps = 5) => {
  const { r, g, b } = hexToRgb(hex);
  const arr = [];

  for (let i = steps; i >= 0; i--) {
    const ratio = i / steps;
    arr.push(
      rgbToHex({
        r: Math.round(r * ratio + 255 * (1 - ratio)),
        g: Math.round(g * ratio + 255 * (1 - ratio)),
        b: Math.round(b * ratio + 255 * (1 - ratio)),
      })
    );
  }

  return arr;
};
  /* ==================== COLOR GENERATORS ==================== */

  const generateHexColor = useCallback(() => {
    let hex = "#";
    for (let i = 0; i < 6; i++) {
      hex += HEX_CHARS[Math.floor(Math.random() * HEX_CHARS.length)];
    }
    return hex;
  }, []);

  const generateRandomColors = useCallback(
    (count) => {
      return Array.from({ length: count }, () => generateHexColor());
    },
    [generateHexColor]
  );

  const generatePalette = useCallback(() => {
    const baseColor = generateHexColor();
    let newColors = [];

    switch (activeMode) {
      case "Random":
        newColors = generateRandomColors(numColors);
        break;
      case "Monochrome":
        newColors = getMonochrome(baseColor, numColors);
        break;
      case "Complementary":
        newColors = getComplementary(baseColor, numColors);
        break;
      case "Analogous":
        newColors = getAnalogous(baseColor, numColors);
        break;
      case "Triadic":
        newColors = getTriadic(baseColor, numColors);
        break;
      case "Split Complementary":
        newColors = getSplitComplementary(baseColor, numColors);
        break;
      default:
        newColors = generateRandomColors(numColors);
    }

    return newColors;
  }, [
    activeMode,
    numColors,
    generateHexColor,
    generateRandomColors,
    getMonochrome,
    getComplementary,
    getAnalogous,
    getTriadic,
    getSplitComplementary,
  ]);

  /* ==================== INITIAL LOAD ==================== */

useEffect(() => {
  if (!hexCode) {
    setColors(generateRandomColors(5));
  }
}, [hexCode, generateRandomColors]);

  useEffect(() => {
    if (hexCode) {
      const urlColors = hexCode
        .split("-")
        .map((c) => "#" + c.replace("#", "").toUpperCase());

      setColors(urlColors);
      setNumColors(urlColors.length);
    }
  }, [hexCode]);

  /* ==================== SPACEBAR GENERATE ==================== */

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.code === "Space") {
        e.preventDefault();

        const newColors = generatePalette();

        setColors((prev) =>
          prev.map((color, index) =>
            lockedColors.includes(color)
              ? color
              : newColors[index]
          )
        );

        setHistory((prev) => [newColors, ...prev].slice(0, 20));
        setHistoryCount((prev) => prev + 1);
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () =>
      window.removeEventListener("keydown", handleKeyDown);
  }, [generatePalette, lockedColors]);

  /* ==================== ACTIONS ==================== */

  const generateColors = () => {
    const newColors = generatePalette();
    setColors(newColors);
    setActivePaletteIndex(null);
    setHistory((prev) => [newColors, ...prev].slice(0, 20));
    setHistoryCount((prev) => prev + 1);
  };

  const handleLockedColors = (color) => {
    setLockedColors((prev) =>
      prev.includes(color)
        ? prev.filter((c) => c !== color)
        : [...prev, color]
    );
  };

  const handleDelete = (index) => {
    if (colors.length <= 2) return;
    const colorToDelete = colors[index];
    const newColors = colors.filter((_, i) => i !== index);

    setColors(newColors);
    setNumColors(newColors.length);
    setLockedColors((prev) =>
      prev.filter((c) => c !== colorToDelete)
    );
  };

const handleAddColor = () => {
  if (colors.length >= 9) return;

  let newPalette = [];

  if (activeMode === "Random") {
    setColors(prev => [...prev, generateHexColor()]);
    setNumColors(prev => prev + 1);
    return;
  }

  const baseColor = colors[0];

  switch (activeMode) {
    case "Monochrome":
      newPalette = getMonochrome(baseColor, colors.length + 1);
      break;

    case "Complementary":
      newPalette = getComplementary(baseColor, colors.length + 1);
      break;

    case "Analogous":
      newPalette = getAnalogous(baseColor, colors.length + 1);
      break;

    case "Triadic":
      newPalette = getTriadic(baseColor, colors.length + 1);
      break;

    case "Split Complementary":
      newPalette = getSplitComplementary(baseColor, colors.length + 1);
      break;

    default:
      newPalette = generateRandomColors(colors.length + 1);
  }

  setColors(newPalette);
  setNumColors(prev => prev + 1);
};

  const getReadableTextColor = (hex) => {
    const { l } = hexToHSL(hex);
    return l > 65 ? "#111" : "#fff";
  };


  return (
    
    <div className="container">
      <Navbar
      generateColors={generateColors}
      showModes={showModes}
      openExport={openExport}
      handleExport={handleExport}
      handleColorModes={handleColorModes}
        historyCount={historyCount}
        showHistory={showHistory}
        onHistoryClick={handleHistoryClick}
      />

<div className="colors-wrapper">
          {colors.map((color, index) => {
            const displayColor =
    activePaletteIndex === index && hoveredColor
      ? hoveredColor
      : color;

  const textColor = getReadableTextColor(displayColor);
          return <div
  key={index}
  className="color-block"
  style={{
    backgroundColor:
      activePaletteIndex === index && hoveredColor
        ? hoveredColor
        : color,
  }}
>
 {index === colors.length - 1 && (
            <button
              className="add-color-btn"
              onClick={handleAddColor}
            >
              <FiPlus />
            </button>
          )}
                 <div className="content">
              <div className="icons">
                <FiCopy
style={{ cursor: "pointer", color: textColor, opacity: 0.4 }}
                  onClick={() => {
                    navigator.clipboard.writeText(color);
                    setCopied(true);
                    setTimeout(() => setCopied(false), 1200);
                  }}
                />

                <FaPalette
style={{ cursor: "pointer", color: textColor, opacity: 0.4 }}
                  onClick={() =>
                    setActivePaletteIndex(
                      activePaletteIndex === index ? null : index
                    )
                  }
                />

               {lockedColors.includes(color) ? (
  <FiLock
    style={{ cursor: "pointer", color: textColor }}
    onClick={() => handleLockedColors(color)}
  />
) : (
  <FiUnlock
    style={{ cursor: "pointer", color: textColor, opacity: 0.4 }}
    onClick={() => handleLockedColors(color)}
  />
)}
                {colors.length > 2 && (

                  <FiX 
style={{ cursor: "pointer", color: textColor, opacity: 0.4 }}
                  onClick={() => handleDelete(index)} />
                )}
              </div>
<h3 style={{ color: textColor }}>
          {displayColor.toUpperCase()}
        </h3>
               {lockedColors.includes(color) ? (
  <FiLock
    style={{ cursor: "pointer", color: textColor }}
    onClick={() => handleLockedColors(color)}
  />
) : (
  <div></div>
)}
              {activePaletteIndex === index && (
                <div className="palette-container">
                  {generateGradient(color, 8).map((c, i, arr) => (
                    <div
  key={i}
  className="palette-color"
  style={{ backgroundColor: c }}

  onMouseEnter={() => setHoveredColor(c)}
  onMouseLeave={() => setHoveredColor(null)}

  onClick={() => {
    navigator.clipboard.writeText(arr.join(", "));
    setCopied(true);
    setTimeout(() => setCopied(false), 1200);
  }}
/>
                  ))}
                </div>
              )}
            </div>
          </div>
})}
      </div>


{/* EXPORT SLIDER */}
<div className="sliders-container">

<div 
className={`export-slider 
  ${openExport ? "active" : ""} 
  ${openExport && showModes ? "pushed-left" : ""}`}>
  <div className="modes-header">
    <h3>Export Palette</h3>
    <FiX onClick={() => setOpenExport(false)} style={{ cursor: "pointer" }} />
  </div>

  <div className="export-options">
    <button onClick={() => exportAsSVG()}>
      <div className="button-content">
        <div className="main-text">
          <FiFile style={{ marginRight: "8px" }} /> SVG
        </div>
        <div className="sub-text">Download as vector file</div>
      </div>
    </button>

    <button onClick={() => exportAsPNG()}>
      <div className="button-content">
        <div className="main-text">
          <FiImage style={{ marginRight: "8px" }} /> PNG
        </div>
        <div className="sub-text">Download as image file</div>
      </div>
    </button>

    <button onClick={() => copyAsURL()}>
      <div className="button-content">
        <div className="main-text">
          <FiLink style={{ marginRight: "8px" }} /> URL
        </div>
        <div className="sub-text">Copy palette link</div>
      </div>
    </button>

    <button onClick={() => downloadTailwindV4()}>
      <div className="button-content">
        <div className="main-text">
          <SiTailwindcss style={{ marginRight: "8px" }} /> Tailwind
        </div>
        <div className="sub-text">Download Tailwind classes</div>
      </div>
    </button>

    <button onClick={() => downloadJSON()}>
      <div className="button-content">
        <div className="main-text">
          <FiCode style={{ marginRight: "8px" }} /> JSON
        </div>
        <div className="sub-text">Download as JSON array</div>
      </div>
    </button>
    <button onClick={() => downloadCSSVariables()}>
      <div className="button-content">
        <div className="main-text">
          <SiCss3 style={{ marginRight: "8px" }} /> CSS
        </div>
        <div className="sub-text">Download as CSS Variables</div>
      </div>
    </button>
  </div>
</div>

{/* MODES SLIDER */}
<div 
className={`modes-slider 
  ${showModes ? "active" : ""} 
  ${showModes && openExport ? "pushed-right" : ""}`}>
  <div className="modes-header">
    <h3>Color Modes</h3>
    <FiX onClick={() => setShowModes(false)} />
  </div>


  <div className="modes-list">
    {colorModes.map((mode, index) => (
      <div
        key={index}
        className={`mode-item ${activeMode === mode ? "selected" : ""}`}
        onClick={() => changeColorModes(mode)}
      >
        <p>{mode}</p>
        {/* {activeMode === mode && <div className="mode-dot" />} */}
      </div>
    ))}
  </div>
</div>
</div>
      {/* HISTORY SLIDER */}
      
      <div className={`history-slider ${showHistory ? "active" : ""}`}>
        {history.map((palette, index) => (
          <div
            key={index}
            className="history-item"
            onClick={() => {
              setColors(palette);
              setShowHistory(false);
            }}
          >
            {palette.map((c, i) => (
              <div
                key={i}
                className="history-color"
                style={{ background: c }}
              />
            ))}
          </div>
        ))}
      </div>

      {copied && <div className="copied-popup">Copied!</div>}
    </div>
  );
};